// ?modal=ads&entry=<ad spend id> (DD-041, TD-088): one client's month of ad spend. The agency's ad account paid it as
// it was spent, so it needs no action; the client pays ad spend back at cost on their invoices. A month a correction left below
// zero is money back: its total reads negative and it lowers that rebill. The agency's own ad spend is its expense, never
// fronted or rebilled (TD-105). Read from the list that opens it: Business expenses lists every client's months of ad spend,
// each with its client's ad spend fronted today, the server's (port Q7, TD-102), so it opens from a pot's month too.
import { MoneyText } from "@/components/ui/MoneyText";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { StaticNotice } from "../jobs/JobParts";
import type { AdSpendFlow } from "../types";
import { OWN_ADS_NOTE, adSpendNote, frontedLine, platformName } from "../utils/expenses";
import { monthLabel } from "../utils/format";

/** What the drawer reads: the months of ad spend listed, each with its client's ad spend fronted today, and today. */
export interface AdSpendData {
  adSpend: AdSpendFlow[];
  today: string;
}

export function AdSpendModal({ entryId, data, onClose }: { entryId: string; data: AdSpendData; onClose: () => void }) {
  const ad = data.adSpend.find((a) => a.id === entryId);
  if (!ad) return <StaticNotice title="Ad spend not found" message="This month of ad spend is no longer listed." onClose={onClose} />;
  return (
    <FinanceDrawer onClose={onClose} title={`Ad spend · ${ad.clientName} · ${monthLabel(ad.month)}`}>
      <div className="space-y-4 text-sm">
        {/* A month below zero reads as what it is: negative spend, money back, in red below. */}
        <p className="text-gray-600">{ad.expense ? OWN_ADS_NOTE : adSpendNote(ad, data.today)}</p>
        <div className="rounded-lg bg-gray-50 divide-y divide-gray-100">
          {ad.platforms.map((p) => (
            <div key={p.platform} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="text-gray-900">{platformName(p.platform)}</span>
              <MoneyText amountMinor={p.amountMinor} />
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 px-3 py-2 font-semibold">
            <span>Total</span>
            <MoneyText amountMinor={ad.amountMinor} strong />
          </div>
        </div>
        {!ad.expense && <p className="text-gray-600">{frontedLine(ad.clientName, ad.frontedMinor)}</p>}
      </div>
    </FinanceDrawer>
  );
}
